import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {AccountContext, AccountContextInterface} from '../../contexts/account-context';
import {useAuth} from '../../contexts/auth.provider';
import {useJobTitle} from '../../contexts/job-title.provider';
import {useOpportunity} from '../../contexts/opportunity.provider';
import {useSkill} from '../../contexts/skill.provider';
import {ROUTES} from '../../constants/routes';
import {
  CreateOpportunityFormData,
  Department,
  EducationRequirement,
  EmploymentType,
  ExperienceLevel,
  OpportunityStatus,
  OpportunityVisibility,
  UploadStatus,
} from '../../models/opportunity';
import {Community} from '../../models/community';
import {ResourceSource} from '../../models';
import {CommunityServiceImpl} from '../../services';

export const useCreateOpportunity = () => {
  const navigate = useNavigate();
  const [communities, setCommunities] = useState<Community[]>([]);
  const [communitiesLoading, setCommunitiesLoading] = useState(false);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<CreateOpportunityFormData | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(UploadStatus.UPLOADING);

  const {accountAuth, accountService} = useContext<AccountContextInterface>(AccountContext);
  const {user} = useAuth();
  const {createJobTitle} = useJobTitle();
  const {createSkill} = useSkill();
  const {createOpportunity} = useOpportunity();
  const communityService = useMemo(() => new CommunityServiceImpl(), []);

  useEffect(() => {
    const accountId = accountAuth?.id ?? user?.id;
    if (!accountId) return;

    let cancelled = false;
    const loadDiscoverCommunities = async () => {
      setCommunitiesLoading(true);
      try {
        const limit = 100;
        let page = 1;
        let total = 0;
        const all: Community[] = [];

        do {
          const res = await communityService.discoverCommunities({limit, page});
          if (res.hasError() || !res.data) break;

          all.push(...res.data.data);
          total = res.data.total ?? all.length;
          page += 1;
        } while (all.length < total);

        if (!cancelled) {
          const uniqueCommunities = Array.from(new Map(all.map(c => [c.id, c])).values());
          setCommunities(uniqueCommunities);
        }
      } finally {
        if (!cancelled) setCommunitiesLoading(false);
      }
    };

    loadDiscoverCommunities();
    return () => {
      cancelled = true;
    };
  }, [accountAuth?.id, user?.id, communityService]);

  const handleSubmit = useCallback(async (values: CreateOpportunityFormData) => {
    setPreviewData(values);
    setShowPreview(true);
  }, []);

  const handleCancel = useCallback(() => {
    navigate(ROUTES.OPPORTUNITIES);
  }, [navigate]);

  const handleVideoUpload = useCallback((_fileName: string, file: File) => {
    setVideoFile(file);
  }, []);

  const handleBackToEdit = useCallback(() => {
    setShowPreview(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowPreview(false);
  }, []);

  const uploadVideoResource = useCallback(
    async (userId: string): Promise<string | undefined> => {
      if (!videoFile) return undefined;

      setIsUploading(true);
      setUploadProgress(0);
      setUploadStatus(UploadStatus.UPLOADING);

      const formData = new FormData();
      formData.append('file', videoFile);
      formData.append('source', ResourceSource.CRED);
      formData.append('type', 'video');

      const onUploadProgress = (progressEvent: any) => {
        if (progressEvent.lengthComputable) {
          const uploadPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(uploadPercent);

          // When upload bytes are complete, switch to processing status
          if (uploadPercent === 100) {
            setUploadStatus(UploadStatus.PROCESSING);
          }
        }
      };

      try {
        const uploadResponse = await accountService.createReccoResource(userId, formData, onUploadProgress);

        if (uploadResponse.hasError()) {
          throw new Error(uploadResponse.error || 'Failed to upload video');
        }

        if (uploadResponse.data && uploadResponse.data.length > 0) {
          const videoUrl = uploadResponse.data[0].id;
          setUploadStatus(UploadStatus.COMPLETE);
          return videoUrl;
        }
        return undefined;
      } finally {
        setIsUploading(false);
        setUploadStatus(UploadStatus.UPLOADING);
      }
    },
    [videoFile, accountService],
  );

  const createJobTitleAndSkills = useCallback(async () => {
    if (!previewData) throw new Error('Missing preview data');

    const jobTitleResult = await createJobTitle(previewData.jobTitle);
    if (!jobTitleResult) {
      throw new Error('Failed to create job title');
    }

    const skillPromises = previewData.requiredSkills.map(skillName => createSkill(skillName));
    const skillResults = await Promise.all(skillPromises);
    const skillIds = skillResults.filter(Boolean).map(result => result.id);

    return {jobTitleResult, skillIds};
  }, [previewData, createJobTitle, createSkill]);

  const createOpportunityWithStatus = useCallback(
    async (status: OpportunityStatus, setLoadingState: (loading: boolean) => void, actionName: string) => {
      if (!previewData || (!accountAuth?.id && !user?.id)) {
        console.error('Missing data or authentication');
        return;
      }

      const userId = accountAuth?.id || user?.id;
      if (!userId) {
        console.error('User ID not available');
        return;
      }

      setLoadingState(true);

      try {
        const videoUrl = await uploadVideoResource(userId);
        const {jobTitleResult, skillIds} = await createJobTitleAndSkills();

        const visibility = (previewData.visibility as OpportunityVisibility) ?? OpportunityVisibility.PUBLIC;
        const restrictedToCommunityIds =
          visibility === OpportunityVisibility.PRIVATE && previewData.restrictedToCommunityIds?.length
            ? previewData.restrictedToCommunityIds
            : [];

        const opportunityResult = await createOpportunity({
          applicationDeadline: new Date(previewData.applicationDeadline),
          department: previewData.department as Department,
          educationRequirements: previewData.educationRequirements as EducationRequirement,
          employmentType: previewData.employmentType as EmploymentType,
          experienceLevel: previewData.experienceLevel as ExperienceLevel,
          jobDescription: previewData.jobDescription,
          jobTitle: jobTitleResult.id,
          location: previewData.location,
          requiredSkills: skillIds,
          restrictedToCommunityIds: visibility === OpportunityVisibility.PUBLIC ? undefined : restrictedToCommunityIds,
          salary: previewData.salary,
          status,
          videoUrl: videoUrl,
          visibility,
        });

        if (!opportunityResult) {
          throw new Error(`Failed to ${actionName.toLowerCase()}`);
        }

        navigate(ROUTES.OPPORTUNITIES);
      } catch (error) {
        console.error(`Error ${actionName.toLowerCase()}:`, error);
        alert(`Failed to ${actionName.toLowerCase()}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setLoadingState(false);
      }
    },
    [previewData, accountAuth, user?.id, uploadVideoResource, createJobTitleAndSkills, createOpportunity, navigate],
  );

  const handleSaveAsDraft = useCallback(async () => {
    await createOpportunityWithStatus(OpportunityStatus.DRAFT, setIsSavingDraft, 'Save as draft');
  }, [createOpportunityWithStatus]);

  const handlePublish = useCallback(async () => {
    await createOpportunityWithStatus(OpportunityStatus.PUBLISHED, setIsPublishing, 'Publish');
  }, [createOpportunityWithStatus]);

  const handleRemoveVideo = useCallback(() => {
    setVideoFile(null);
  }, []);

  const handleVideoUploadInModal = useCallback((file: File) => {
    setVideoFile(file);
  }, []);

  return {
    // State
    communities,
    communitiesLoading,
    videoFile,
    showPreview,
    previewData,
    isPublishing,
    isSavingDraft,
    uploadProgress,
    isUploading,
    uploadStatus,

    // Handlers
    handleSubmit,
    handleCancel,
    handleVideoUpload,
    handleBackToEdit,
    handleCloseModal,
    handleSaveAsDraft,
    handlePublish,
    handleRemoveVideo,
    handleVideoUploadInModal,

    // Setters
    setVideoFile,
    setShowPreview,
    setPreviewData,
  };
};
